import { CommonModule } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { AuthService } from '@/app/core/services/auth.service';
import { AppMenuitem } from './app.menuitem';

import {
    HOME_MENU,
    ORDERS_NOTES_MENU,
    OPERATIONAL_PLANNING_MENU,
    ASSETS_MENU,
    MATERIALS_MENU,
    MONITORING_MENU,
    ANALYTICS_MENU,
    SETTINGS_MENU,
    ADMIN_MENU,
    HELP_MENU
} from './app.menu-json';

/** Menu node shape (template/menuitem use additional PrimeNG fields). */
export interface AppMenuModelItem {
    label?: string;
    labelKey?: string;
    icon?: string;
    path?: string;
    routerLink?: string[];
    url?: string | string[];
    target?: string;
    separator?: boolean;
    /** If set, the user must have at least one of these claims (JWT roles) to see the item. */
    role?: string[];
    /** Marks the item as non-navigable (used for `[F2]` / Fase 2 items that are visible but not yet clickable). */
    disabled?: boolean;
    /** CSS class applied to the item's `<li>`; used to render the "F2" badge without touching the menu template. */
    badgeClass?: string;
    items?: AppMenuModelItem[];
    [key: string]: unknown;
}

function filterMenuByRoles(items: AppMenuModelItem[], hasClaim: (claim: string) => boolean): AppMenuModelItem[] {
    const mapped = items
        .map((item) => {
            if (item.separator) {
                return item;
            }

            const required = item.role?.filter(Boolean) ?? [];
            const allowedByRole = required.length === 0 || required.some((r) => hasClaim(r));

            if (item.items && item.items.length > 0) {
                if (!allowedByRole) {
                    return null;
                }
                const nextItems = filterMenuByRoles(item.items, hasClaim);
                const hasDirectLink = !!(item.routerLink?.length || item.url);
                if (nextItems.length === 0 && !hasDirectLink) {
                    return null;
                }
                return { ...item, items: nextItems };
            }

            if (!allowedByRole) {
                return null;
            }
            return { ...item };
        })
        .filter((item): item is AppMenuModelItem => item !== null);

    return collapseAdjacentSeparators(mapped);
}

function collapseAdjacentSeparators(items: AppMenuModelItem[]): AppMenuModelItem[] {
    const out: AppMenuModelItem[] = [];
    let previousWasSeparator = true;

    for (const item of items) {
        if (item.separator) {
            if (!previousWasSeparator && out.length > 0) {
                out.push(item);
                previousWasSeparator = true;
            }
        } else {
            previousWasSeparator = false;
            out.push(item);
        }
    }

    while (out.length > 0 && out[out.length - 1].separator) {
        out.pop();
    }

    return out;
}




// Order follows the Mapa do Site v3 tree (section 3): 8 work modules + Administração + Ajuda.
const APP_MENU_SOURCE: AppMenuModelItem[] = [
    HOME_MENU,


    ORDERS_NOTES_MENU,


    OPERATIONAL_PLANNING_MENU,

    ASSETS_MENU,

    MATERIALS_MENU,

    MONITORING_MENU,

    ANALYTICS_MENU,

    SETTINGS_MENU,

    ADMIN_MENU,

    HELP_MENU,
];

@Component({
    selector: '[app-menu]',
    standalone: true,
    imports: [CommonModule, AppMenuitem, RouterModule],
    template: `<ul class="layout-menu">
        @for (item of model(); track $index) {
            @if (!item.separator) {
                <li app-menuitem [item]="item" [root]="true" [level]="1"></li>
            } @else {
                <li class="menu-separator"></li>
            }
        }
    </ul> `
})
export class AppMenu {
    private readonly authService = inject(AuthService);

    readonly model = computed(() => {
        this.authService.currentUser();
        return filterMenuByRoles(APP_MENU_SOURCE, (claim) => this.authService.hasClaim(claim));
    });
}
